import React from 'react';
import DatePicker from 'react-datepicker';
import { Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { InterspireSegment } from '../../types';
import { EmailEntry, TemplateBuild } from './eventBuildUtils';
import SegmentSearchPicker from './SegmentSearchPicker';

interface Props {
  columnIndex: number;
  templateName: string;
  build: TemplateBuild;
  segments: InterspireSegment[];
  interspireEnabled: boolean;
  onUpdateBuild: (patch: Partial<TemplateBuild>) => void;
  onUpdateEmail: (emailId: string, patch: Partial<EmailEntry>) => void;
  onFirstDate: (date: Date | null) => void;
  onAddEmail: () => void;
  onRemoveEmail: (emailId: string) => void;
  onUpdateSubjects: () => void;
  onPreview: () => void;
}

const fieldClass =
  'block w-full rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1 px-2';

const ScheduleTimeline: React.FC<Props> = ({
  columnIndex,
  templateName,
  build,
  segments,
  interspireEnabled,
  onUpdateBuild,
  onUpdateEmail,
  onFirstDate,
  onAddEmail,
  onRemoveEmail,
  onUpdateSubjects,
  onPreview,
}) => {
  return (
    <div className="h-full bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-4 flex flex-col">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-3">
        {templateName}
        <span className="ml-1.5 text-[10px] font-normal text-gray-400 dark:text-slate-500">
          #{columnIndex + 1}
        </span>
      </h3>

      <label className="block text-[10px] font-medium text-gray-500 dark:text-slate-400 mb-0.5">
        Segment
      </label>
      <SegmentSearchPicker
        segments={segments}
        value={build.segmentId}
        onChange={(segmentId) => onUpdateBuild({ segmentId })}
        disabled={!interspireEnabled}
      />

      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-[10px] font-medium text-gray-500 dark:text-slate-400">
          Email Schedule ({build.emails.length})
        </p>
        {build.emails.length > 1 && (
          <p className="text-[9px] text-blue-600 dark:text-blue-300">
            First date sets later emails (+2 weeks)
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-x-auto rounded border border-gray-200 dark:border-slate-600">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 dark:bg-slate-900/60 text-[10px] uppercase tracking-wide text-gray-500 dark:text-slate-400">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium w-[7.5rem]">Date</th>
              <th className="px-2 py-1.5 text-left font-medium w-[4.5rem]">Time</th>
              <th className="px-2 py-1.5 text-left font-medium">Subject</th>
              <th className="px-2 py-1.5 text-right font-medium w-10"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {build.emails.map((email, index) => (
              <tr key={email.id} className="bg-white dark:bg-slate-800 align-top">
                <td className="px-2 py-1.5">
                  <DatePicker
                    selected={email.scheduledDate}
                    onChange={(date) => {
                      if (index === 0) onFirstDate(date);
                      else onUpdateEmail(email.id, { scheduledDate: date });
                    }}
                    dateFormat="MMM d"
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="time"
                    value={email.scheduledTime}
                    onChange={(e) => onUpdateEmail(email.id, { scheduledTime: e.target.value })}
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="text"
                    value={email.subject}
                    onChange={(e) => onUpdateEmail(email.id, { subject: e.target.value })}
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => onRemoveEmail(email.id)}
                    disabled={build.emails.length <= 1}
                    className="inline-flex p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30"
                    title="Remove email"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onAddEmail}
          disabled={build.emails.length >= 10}
          className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded-md ${
            build.emails.length >= 10
              ? 'border-gray-200 dark:border-slate-700 text-gray-400 cursor-not-allowed'
              : 'border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600'
          }`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Email
        </button>
        <button
          type="button"
          onClick={onUpdateSubjects}
          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-slate-600 text-xs font-medium rounded-md text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Update Subjects
        </button>
        <button
          type="button"
          onClick={onPreview}
          className="ml-auto inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm"
        >
          <Eye className="h-3.5 w-3.5 mr-1.5" />
          Preview
        </button>
      </div>
    </div>
  );
};

export default ScheduleTimeline;
